import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/axios';
import { Calendar, ArrowLeft, ArrowRight } from "lucide-react";
import { motion } from "motion/react";
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { sanitizeHtml } from '../../utils/sanitize';
import { PageHero } from '../../components/common/PageHero';

export const PosterDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const { t } = useTranslation();

    const { data: poster, isLoading, isError } = useQuery({
        queryKey: ['publicPoster', slug],
        queryFn: async () => {
            const response = await apiClient.get(`/public/posters/${slug}`);
            return response.data.data;
        },
        enabled: !!slug,
        retry: false
    });

    useEffect(() => {
        if (poster?.title) {
            document.title = `${poster.title} | Mosaik Offenbach`;
        }
    }, [poster?.title]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-32 min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (isError || !poster) {
        return <Navigate to="/veranstaltungen" replace />;
    }

    return (
        <div className="flex flex-col bg-background min-h-screen">
            <PageHero
                title={poster.title}
                imageUrl={poster.imageUrl}
                breadcrumb={[
                    { label: t('common.home'), to: '/' },
                    { label: t('events.breadcrumb'), to: '/veranstaltungen' },
                    { label: poster.title }
                ]}
            />

            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 sm:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    {/* Media Section */}
                    <motion.div 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="relative rounded-3xl overflow-hidden shadow-xl bg-background border border-border aspect-[3/4]"
                    >
                        <img 
                            src={resolveAssetUrl(poster.imageUrl) || 'https://via.placeholder.com/800x1200?text=Event'} 
                            alt={poster.title}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    </motion.div>

                    {/* Content Section */}
                    <motion.div 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex flex-col"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold w-fit mb-6">
                            <Calendar className="h-4 w-4" />
                            {t('events.event')}
                        </div>

                        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
                            {poster.title}
                        </h1>

                        <div
                            className="prose prose-lg prose-primary max-w-none text-foreground/80 leading-relaxed mb-10"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(poster.description) }}
                        />

                        <div className="flex flex-wrap gap-4 mt-auto pt-10 border-t border-border/50">
                            {poster.isRedirect ? (
                                <a 
                                    href={poster.pageUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-2xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    {t('events.tickets_info')}
                                    <ArrowRight className="h-5 w-5" />
                                </a>
                            ) : (
                                <button 
                                    onClick={() => window.open(resolveAssetUrl(poster.imageUrl), '_blank')}
                                    className="inline-flex items-center gap-2 rounded-2xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    {t('events.view_full')}
                                    <ArrowRight className="h-5 w-5" />
                                </button>
                            )}
                            
                            <Link 
                                to="/veranstaltungen"
                                className="inline-flex items-center gap-2 rounded-2xl bg-background border border-border px-8 py-4 text-base font-semibold text-foreground transition-all hover:bg-secondary/50"
                            >
                                <ArrowLeft className="h-5 w-5" />
                                {t('events.back_to_overview')}
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};
